import { forwardRef, useMemo } from "react";
import { type Column } from "@racoon/ui";
import { useT, Namespace, type KeysOf } from "@racoon/i18n";
import {
  CounterpartyReportRow,
  rowContracts,
  rowJobTypes,
  rowTariff,
  rowTariffAmount,
  totalCollectedVolume,
  useCounterpartyReportRows,
} from "../model/use-counterparty-report-rows";
import { useReportHyperlinks } from "../model/use-report-hyperlinks";
import { amountCell, byNumber, numberColumn } from "../model/job-report";
import { ReportTable, ReportTableHandle, ReportTableProps } from "./report-table";

/** A report row, or the totals line that closes the exports. */
type CounterpartyExportRow = CounterpartyReportRow & { isTotal?: boolean };

// A tariff of 0 is a real value; only a counterparty with none reads "—".
const textTariff = (tariff: number | string) => (tariff === "" ? "—" : tariff);

/** «Реестр контрагентов»: each active counterparty with its addresses, contracts, tariffs and what was collected. */
export const CounterpartyReportTable = forwardRef<ReportTableHandle, ReportTableProps>(
  (props, ref) => {
    const t = useT();
    const { rows, isLoading, error } = useCounterpartyReportRows(props.startDate, props.endDate);
    const { customerCell, entityCards } = useReportHyperlinks();

    const columns: Column<CounterpartyExportRow>[] = useMemo(() => {
      // A sortable text column: «—» when blank, and blank on the totals line.
      const text = (
        id: string,
        key: KeysOf<Namespace.Analytics>,
        value: (row: CounterpartyExportRow) => string,
        width: number,
      ): Column<CounterpartyExportRow> => ({
        id,
        header: t(Namespace.Analytics, key),
        accessor: (row) => (row.isTotal ? "" : value(row) || "—"),
        width,
        sortable: true,
      });

      return [
        numberColumn(
          t(Namespace.Analytics, "columns.number"),
          t(Namespace.Analytics, "collection.totals.label"),
        ),
        text("bin", "columns.bin", (row) => row.bin, 140),
        {
          ...text("name", "table.customer", (row) => row.name, 220),
          cell: ({ row }) => customerCell(row.original.counterpartyId, row.original.name),
        },
        text("customerType", "counterpartyRegistry.customerType", (row) => row.customerType, 170),
        text(
          "juridicalAddress",
          "counterpartyRegistry.jurAddress",
          (row) => row.juridicalAddress,
          240,
        ),
        text("factAddress", "counterpartyRegistry.factAddress", (row) => row.factAddress, 240),
        text(
          "siteAddresses",
          "counterpartyRegistry.siteAddresses",
          (row) => row.siteAddresses,
          320,
        ),
        text("containers", "siteRegistry.containers", (row) => row.containers, 240),
        {
          id: "collectedVolume",
          header: t(Namespace.Analytics, "counterpartyRegistry.collectedVolume"),
          accessor: (row) => amountCell(row.collectedVolume, 2, row.isTotal),
          width: 160,
          sortable: true,
          sortFn: byNumber((row) => row.collectedVolume),
        },
        text("contract", "collection.contract", rowContracts, 220),
        text("jobType", "counterpartyRegistry.jobType", rowJobTypes, 220),
        {
          id: "tariff",
          header: t(Namespace.Analytics, "counterpartyRegistry.tariff"),
          accessor: (row) => (row.isTotal ? "" : textTariff(rowTariff(row))),
          width: 220,
          sortable: true,
          sortFn: byNumber(rowTariffAmount),
        },
      ];
    }, [t, customerCell]);

    // The totals line copies a row; every column but the volume blanks it.
    const exportRows = useMemo<CounterpartyExportRow[]>(
      () =>
        rows.length
          ? [
              ...rows,
              {
                ...rows[0],
                isTotal: true,
                id: "total",
                collectedVolume: totalCollectedVolume(rows),
                tariffs: [],
              },
            ]
          : rows,
      [rows],
    );

    return (
      <ReportTable
        {...props}
        exportRef={ref}
        settingsKey="counterparty_registry_report"
        reportName={t(Namespace.Analytics, "reports.counterpartyRegistry")}
        columns={columns}
        rows={rows}
        exportRows={exportRows}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
      >
        {entityCards}
      </ReportTable>
    );
  },
);
